import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Driveway } from "building-types/driveways/drivewayTypes";
import { DRIVEWAY_DEPTH } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { faceNew } from "geom/brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, DIST_Z_FIGHT, ZERO_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal } from "geom/extrude";
import { planarGraphHalfEdgeEval, planarGraphHalfEdgeNormal } from "geom/planarGraph";
import { planeNewXY } from "geom/plane";
import { polyLine2ByTwoPoints, polyLine2Extend, polyLine2Length, polyLine2Reverse } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dot, vec2Perp } from "geom/vec2";
import { isGarageDoorOperationType } from "../elements/doors/doorUtils";
import { drivewayNew } from "../elements/driveway";
import { groundLevelId } from "../elements/level/levelTools";
import memo from "../utils/memo";

const DRIVEWAY_MERGE_THRESHOLD = 100;
const DRIVEWAY_MAX_DEPTH = 1200;

function select({
  settings,
  planarGraphs,
  doorPlacements: doors,
  buildingBounds,
  levels,
  doc,
}: Pick<Building, "settings" | "planarGraphs" | "doorPlacements" | "buildingBounds" | "levels" | "doc">) {
  return {
    levels,
    doors,
    buildingBounds,
    planarGraphs,
    garageSettings: settings.garage,
    docSites: doc.sites,
  };
}

function generate({
  levels,
  doors,
  buildingBounds,
  planarGraphs,
  garageSettings,
  docSites,
}: ReturnType<typeof select>): {
  driveways: Table<Driveway>;
} {
  const driveways: RWTable<Driveway> = {};

  if (Object.values(levels).length === 0) {
    return { driveways };
  }

  if (Object.values(docSites).length !== 0) {
    return { driveways };
  }

  /**
   * TODO:-move this block to generateDrivewaysTools
   */
  const garageLevelIndex = groundLevelId(levels);
  if (!garageLevelIndex) {
    return { driveways };
  }

  const planarGraph = planarGraphs[garageLevelIndex].planarGraph;
  const garageLevel = levels[garageLevelIndex];
  const garageElevation = garageLevel.floorElevation + garageLevel.levelDatumsGenerated.grade;

  // Figure out which doors need driveways.
  const garageDoors: { [halfEdgeIndex: number]: GeneratedDoorPlacement[] } = {};
  for (const doorId in doors) {
    const door = doors[doorId];
    if (!door.frontRoom) {
      continue;
    }
    if (door.frontRoom.level.id !== garageLevelIndex) {
      continue;
    }
    if (isGarageDoorOperationType(door) && door.opening) {
      const includeDriveway = door.includeDriveway ?? door.descriptor.includeDriveway;
      if (!includeDriveway) {
        continue;
      }

      if (garageDoors[door.halfEdgeIndex] === undefined) {
        garageDoors[door.halfEdgeIndex] = [door];
      } else {
        garageDoors[door.halfEdgeIndex].push(door);
      }
    }
  }

  // Consolidate doors that share the same wall into the same driveway
  const drivewayInfo: {
    normalX: number;
    normalY: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }[] = [];
  for (const halfEdgeIndex in garageDoors) {
    const doors = garageDoors[halfEdgeIndex];
    doors.sort((a, b) => {
      return (a.opening?.back.startParam ?? 0) - (b.opening?.back.startParam ?? 0);
    });
    const hei = Number(halfEdgeIndex);

    const firstDoor = doors[0].opening;
    const lastDoor = doors[doors.length - 1].opening;

    assertIsNotUndefined(firstDoor);
    assertIsNotUndefined(lastDoor);

    const [startX, startY] = planarGraphHalfEdgeEval(planarGraph, hei, firstDoor.back.startParam);
    const [endX, endY] = planarGraphHalfEdgeEval(planarGraph, hei, lastDoor.back.endParam);
    const [normalX, normalY] = planarGraphHalfEdgeNormal(planarGraph, hei);
    drivewayInfo.push({ startX, startY, endX, endY, normalX, normalY });
  }

  // Adjust driveways so they join if they are close together.
  drivewayInfo.sort((a, b) => {
    // Sort by direction.
    const aDir = vec2Dot(a.normalX, a.normalY, 0, -1);
    const bDir = vec2Dot(b.normalX, b.normalY, 0, -1);
    if (aDir - bDir < ZERO_EPSILON) {
      // Sort by furthest from house in normal direction.
      const aDist = vec2Dot(a.startX, a.startY, a.normalX, a.normalY);
      const bDist = vec2Dot(b.startX, b.startY, a.normalX, a.normalY);
      return bDist - aDist; // so we get greater first
    } else {
      return aDir - bDir;
    }
  });

  // Try to expand driveways that are closer to the street to meet the ones that
  // started closer to the house.
  for (let i = 0, ic = drivewayInfo.length; i < ic; i++) {
    const toAdjust = drivewayInfo[i];
    const [searchX, searchY] = vec2Perp(toAdjust.normalX, toAdjust.normalY);
    const toAdjustStart = vec2Dot(toAdjust.startX, toAdjust.startY, searchX, searchY);
    const toAdjustEnd = vec2Dot(toAdjust.endX, toAdjust.endY, searchX, searchY);

    let sameNormal = true;
    for (let j = i + 1; j < ic; j++ && sameNormal) {
      // Make sure they are pointed the same way.
      const toMatch = drivewayInfo[j];
      sameNormal =
        vec2Dot(toAdjust.normalX, toAdjust.normalY, toMatch.normalX, toMatch.normalY) >= 1.0 - DIST_EPSILON_SQUARED;
      if (sameNormal) {
        const toMatchStart = vec2Dot(toMatch.startX, toMatch.startY, searchX, searchY);
        const toMatchEnd = vec2Dot(toMatch.endX, toMatch.endY, searchX, searchY);
        const startAdjust = toMatchEnd - toAdjustStart;
        if (startAdjust < DRIVEWAY_MERGE_THRESHOLD && startAdjust > 0) {
          toAdjust.startX += startAdjust * searchX;
          toAdjust.startY += startAdjust * searchY;
        } else {
          const endAdjust = toAdjustEnd - toMatchStart;
          if (endAdjust < DRIVEWAY_MERGE_THRESHOLD && endAdjust > 0) {
            toAdjust.endX -= endAdjust * searchX;
            toAdjust.endY -= endAdjust * searchY;
          }
        }
      }
    }
  }

  // Generate driveways!
  for (const info of drivewayInfo) {
    const { normalX, normalY, startX, startY, endX, endY } = info;
    const doorLine = polyLine2ByTwoPoints(startX, startY, endX, endY);
    polyLine2Extend(doorLine, garageSettings.drivewayDoorOffset, garageSettings.drivewayDoorOffset);

    let driveway: PolyLine2;
    // Driveway is front or back.
    if (Math.abs(normalY) > 1.0 - DIST_EPSILON) {
      const siteEnd = buildingBounds.centroidY + DRIVEWAY_MAX_DEPTH * 0.5 * normalY;
      const drivewayLength = Math.abs(siteEnd - startY);

      driveway = polyLine2New([
        doorLine[0],
        doorLine[1],
        doorLine[2],
        doorLine[3],
        doorLine[2] + normalX * drivewayLength,
        doorLine[3] + normalY * drivewayLength,
        doorLine[0] + normalX * drivewayLength,
        doorLine[1] + normalY * drivewayLength,
        doorLine[0],
        doorLine[1],
      ]);
    } else {
      const sideStart = doorLine[1] > doorLine[3] ? doorLine.slice(0, 2) : doorLine.slice(2, 4);
      const sideEnd = doorLine[1] < doorLine[3] ? doorLine.slice(0, 2) : doorLine.slice(2, 4);
      const drivewayNormalY = -1; // driveway normal is 0, -1 right now

      const siteEnd = buildingBounds.centroidY + DRIVEWAY_MAX_DEPTH * -0.5;
      const drivewayLength = Math.abs(siteEnd - sideStart[1]);

      const houseEdge = normalX < 0 ? buildingBounds.aabb.minX : buildingBounds.aabb.maxX;
      const borderEdge = houseEdge + normalX * garageSettings.sideDrivewayMargin;

      const drivewayWidth = polyLine2Length(doorLine);
      const sideOut = borderEdge + normalX * drivewayWidth;

      driveway = polyLine2New([
        sideStart[0],
        sideStart[1],
        sideOut,
        sideStart[1],
        sideOut,
        sideStart[1] + drivewayLength * drivewayNormalY,
        borderEdge,
        sideStart[1] + drivewayLength * drivewayNormalY,
        borderEdge,
        sideEnd[1],
        sideEnd[0],
        sideEnd[1],
        sideStart[0],
        sideStart[1],
      ]);
      if (normalX < 0) {
        driveway = polyLine2Reverse(driveway);
      }
    }

    const xyPlane = planeNewXY(0, 0, garageElevation + DIST_Z_FIGHT);
    const drivewayFace = faceNew([driveway], xyPlane);
    const ret = drivewayNew();
    ret.exterior.add(extrudeFaceAlongNormal(drivewayFace, -DRIVEWAY_DEPTH), exteriorMaterialOverrideNew("driveway0"));
    driveways[ret.id] = ret;
  }

  return { driveways };
}

export default memo(select, generate);
